import { useEffect, useState } from "react"

import { bbox } from "@turf/turf"

import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { POINT_MIN_BBOX_WIDTH, useMapContext } from "@l2r-front/l2r-map"
import { useNetworksDispatchContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useApplicationStateContext } from "@l2r-front/l2r-utils"

export function PointSelectListener(props) {

    const {
        pointFeature,
        mapId,
        minBbboxWidth = POINT_MIN_BBOX_WIDTH,
    } = props

    const [featureBBDisplayed, setFeatureBBDisplayed] = useState(false)
    const [mapState, mapDispatch] = useMapContext()
    const { mapBoundingBoxToFit, storedBoundingBox } = mapState
    const { getMapRef, resetMapBoundingBox, setMapBoundingBoxToFit } = mapDispatch
    const { setSegmentFeatureIdClicked } = useImageCollectionDispatchContext()
    const { setSelectedLinearLocations } = useNetworksDispatchContext()
    const { navigationOrigin } = useApplicationStateContext()
    const isMobile = useIsMobileDevice()
    const map = getMapRef(mapId)?.getMap()

    useEffect(() => {
        if (!!mapBoundingBoxToFit?.[mapId] || !pointFeature || (featureBBDisplayed === pointFeature.id && navigationOrigin !== NavigationOrigins.DETAIL_FROM_DETAIL)) {
            return
        }

        const pointLinearLocation = {
            ...pointFeature,
            properties: {
                ...pointFeature.properties,
                linearLocation: JSON.stringify([pointFeature.linear_location]),
            },
        }

        if (pointFeature) {
            setSegmentFeatureIdClicked(pointFeature.id)
            setSelectedLinearLocations(pointLinearLocation)

            const [minLng, minLat, maxLng, maxLat] = bbox(pointFeature.geometry)
            const lngOffset = Math.max((minBbboxWidth - (maxLng - minLng)), 0) / 2
            const pointBoundingBox = {
                minLng: minLng - lngOffset,
                minLat,
                maxLng: maxLng + lngOffset,
                maxLat,
            }

            if (map) {
                const newCameraWithBounds = map.cameraForBounds([
                    [pointBoundingBox?.minLng, pointBoundingBox?.minLat],
                    [pointBoundingBox?.maxLng, pointBoundingBox?.maxLat],
                ])
                const currentStoredBoundingBox = storedBoundingBox?.network || storedBoundingBox?.road || storedBoundingBox?.details
                const currentZoom = isMobile ? currentStoredBoundingBox?.zoom || 0 : map.getZoom()
                const newZoom = Math.max(newCameraWithBounds.zoom, currentZoom)
                setMapBoundingBoxToFit(pointBoundingBox, newZoom)
                resetMapBoundingBox()
                setFeatureBBDisplayed(pointFeature.id)
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, pointFeature, mapId, map])

    return null
}

PointSelectListener.propTypes = {
    mapId: PropTypes.string.isRequired,
    pointFeature: PropTypes.shape({
        geometry: PropTypes.geoJsonPoint.isRequired,
        id: PropTypes.string.isRequired,
        linear_location: PropTypes.linearLocation.isRequired,
        type: PropTypes.string.isRequired,
    }),
    minBbboxWidth: PropTypes.number,
}